import { BRAND_NAME } from "@/lib/brand";
import type { ResearchIconName } from "@/lib/icons/research-icons";
import type { LandingTextValue } from "@/lib/landing/puck/styled-text";
import { LANDING_FAQ_ITEMS } from "@/lib/seo/faq-content";
import { DEFAULT_HERO } from "@/lib/landing/puck/hero-defaults";
import { DEFAULT_HERO_SPLIT } from "@/lib/landing/puck/hero-split-defaults";
import { DEFAULT_PARTNERS } from "@/lib/landing/puck/partners-defaults";
import { DEFAULT_ROW_GRID } from "@/lib/landing/puck/row-grid-defaults";
import { createDefaultFooterColumns } from "@/lib/landing/puck/footer-defaults";
import {
  DEFAULT_CONTACTS_HERO,
  DEFAULT_YANDEX_MAP,
} from "@/lib/landing/puck/contacts-defaults";
import { DEFAULT_LINK_LIST } from "@/lib/landing/puck/link-list-defaults";

export {
  DEFAULT_HERO,
  DEFAULT_HERO_STATS,
  mergeHeroProps,
  type LandingHeroStat,
} from "@/lib/landing/puck/hero-defaults";

export {
  DEFAULT_HERO_SPLIT,
  DEFAULT_HERO_SPLIT_HIGHLIGHTS,
} from "@/lib/landing/puck/hero-split-defaults";

export {
  DEFAULT_PARTNERS,
  DEFAULT_PARTNERS_ITEMS,
} from "@/lib/landing/puck/partners-defaults";

export { DEFAULT_ROW_GRID } from "@/lib/landing/puck/row-grid-defaults";
export { DEFAULT_COMPARISON } from "@/lib/landing/puck/comparison-defaults";

export const DEFAULT_PAS = {
  sectionId: "problem",
  title: "Почему до 90% идей закрываются в первый год",
  subtitle:
    "Три типичных тупика до запуска — и как Идеалитика закрывает их за несколько минут, а не за недели.",
  footer:
    "Мы создали инструмент, который даёт ответ за 10 минут с методологией, цифрами и гарантией возврата.",
  backgroundImageUrl: "",
  layout: "standard" as const,
  items: [
    {
      icon: "clock" as ResearchIconName,
      title: "Агентство дорого и долго",
      text: "Классическое исследование — недели ожидания и бюджет, который у стартапа уходит на MVP. Решение нужно было вчера.",
    },
    {
      icon: "messageCircleQuestion" as ResearchIconName,
      title: "ChatGPT без методологии",
      text: "Красивый текст без ссылок на источники и цифр по РФ. Это догадки, а не исследование для инвестора.",
    },
    {
      icon: "alertTriangle" as ResearchIconName,
      title: "Риск невалидной идеи",
      text: "Вы сделали MVP, вложили деньги, а рынок в 10 раз меньше, чем казалось.",
    },
  ],
};

export const DEFAULT_PAINS = {
  sectionId: "pains",
  title: "Знакомо?",
  subtitle: "Если хотя бы одно из этого про вас — дальше будет решение.",
  backgroundImageUrl: "",
  items: [
    {
      icon: "creditCard" as ResearchIconName,
      title: "Дорогие исследования",
      text: "Агентство — от десятков до сотен тысяч. Для стартапа и МСП это часто неподъёмно.",
    },
    {
      icon: "clock" as ResearchIconName,
      title: "Долго ждать",
      text: "Пока готовят отчёт, рынок уже меняется. Нужен ответ быстрее цикла агентства.",
    },
    {
      icon: "layers" as ResearchIconName,
      title: "ChatGPT без структуры",
      text: "Много текста без методологии, scoring и ссылок — непонятно, что с этим делать.",
    },
    {
      icon: "shieldAlert" as ResearchIconName,
      title: "Нет объективности",
      text: "Вы влюблены в идею. Нужен честный взгляд с цифрами, а не с энтузиазмом.",
    },
    {
      icon: "alertTriangle" as ResearchIconName,
      title: "Страх потерять деньги",
      text: "Готовы вложить в разработку, но не уверены в спросе. Сначала — оценка ниши.",
    },
    {
      icon: "fileText" as ResearchIconName,
      title: "Нет данных для инвестора",
      text: "Инвестор просит отчёт по рынку. Скриншот из чата не впечатляет.",
    },
    {
      icon: "gitCompare" as ResearchIconName,
      title: "Сложно сравнить ниши",
      text: "Несколько идей — и ни одной цифры рядом. Как выбрать, куда вкладываться?",
    },
    {
      icon: "trendingUp" as ResearchIconName,
      title: "Конкуренты не ждут",
      text: "Пока думаете, рынок уходит вперёд. Нужна ясность по окну входа.",
    },
  ],
};

export type LandingAudienceItem = {
  icon: ResearchIconName | string;
  title: LandingTextValue;
  text: LandingTextValue;
  imageUrl?: string;
  /** visual: фото-плитка или CTA-плитка (без фото). */
  cardStyle?: "default" | "cta";
  ctaLabel?: LandingTextValue;
  ctaHref?: string;
};

export const DEFAULT_AUDIENCE = {
  sectionId: "audience",
  title: "Для кого",
  subtitle: "Один инструмент — разные сценарии. Найдите свой.",
  backgroundImageUrl: "",
  layout: "grid" as const,
  items: [
    {
      icon: "zap" as ResearchIconName,
      title: "Фаундер",
      text: "Проверьте идею перед инвестициями: scoring по 6 критериям и оценка ёмкости рынка.",
      ctaLabel: "Проверить гипотезу",
      ctaHref: "/reports/create",
    },
    {
      icon: "users" as ResearchIconName,
      title: "МСП / solo",
      text: "Оцените нишу и конкурентов перед запуском — отчёт с рекомендацией go/no-go.",
      ctaLabel: "Создать отчёт",
      ctaHref: "/reports/create",
    },
    {
      icon: "layers" as ResearchIconName,
      title: "Продуктовая команда",
      text: "Быстрый анализ рынка для нового направления. Экспорт PDF для руководства.",
      ctaLabel: "Запустить анализ",
      ctaHref: "/reports/create",
    },
    {
      icon: "building" as ResearchIconName,
      title: "Акселератор / банк",
      text: "White-label и объёмные исследования для резидентов — обсудим пилот.",
      ctaLabel: "Обсудить пилот",
      ctaHref: "/billing/enterprise",
    },
    {
      icon: "search" as ResearchIconName,
      title: "Консультант",
      text: "Генерируйте методологичные отчёты для клиентов за минуты, а не дни.",
      ctaLabel: "Попробовать",
      ctaHref: "/reports/create",
    },
  ] satisfies LandingAudienceItem[],
};

export type LandingFeatureItem = {
  icon: ResearchIconName | string;
  title: LandingTextValue;
  description: LandingTextValue;
  metric?: LandingTextValue;
  /** Скриншот вкладки (layout tabs). */
  imageUrl?: string;
};

export const DEFAULT_FEATURES_ITEMS = [
  {
    icon: "radar" as ResearchIconName,
    title: "Скоринг по 6 критериям",
    description:
      "TAM, рост, SEO-спрос, окно входа, монетизация и давление конкуренции — с весами и итоговой оценкой.",
    metric: "0–10",
  },
  {
    icon: "link" as ResearchIconName,
    title: "Источники и уровень уверенности",
    description:
      "Утверждения привязаны к открытым данным РФ. Прозрачные допущения — ответ на барьер доверия к LLM.",
    metric: "РФ",
  },
  {
    icon: "search" as ResearchIconName,
    title: "Wordstat по SEO-кластерам",
    description:
      "Реальные показы Яндекса по ключам из отчёта: ВЧ, СЧ, НЧ — не выдуманные цифры из промпта.",
    metric: "SEO",
  },
  {
    icon: "barChart" as ResearchIconName,
    title: "TAM, конкуренты, бенчмарки цен",
    description:
      "Карта рынка, таблица игроков и ориентиры по тарифам — как в кабинетном исследовании, но за минуты.",
    metric: "до 10",
  },
  {
    icon: "gitCompare" as ResearchIconName,
    title: "Сравнение гипотез",
    description:
      "Несколько идей в одном портфеле: сравните ниши до выбора направления или запуска MVP.",
    metric: "портфель",
  },
  {
    icon: "shieldCheck" as ResearchIconName,
    title: "Бизнес-сводка, action pack и PDF",
    description:
      "Тарифы MVP, риски, GTM и экспорт PDF для команды или инвестора.",
    metric: "PDF",
  },
] satisfies LandingFeatureItem[];

export const DEFAULT_CASES = {
  sectionId: "cases",
  title: "Примеры отчётов",
  subtitle: "Готовые исследования — откройте PDF и оцените формат.",
  backgroundImageUrl: "",
  source: "manual" as const,
  reportTypes: [] as string[],
  industries: [] as string[],
  limit: 6,
  layout: "grid" as const,
  items: [] as {
    title: string;
    summary: string;
    coverUrl: string;
    pdfUrl: string;
  }[],
};

export const DEFAULT_VIDEO = {
  sectionId: "video",
  title: "Посмотрите, как работает сервис «Идеалитика»",
  subtitle:
    "Короткий обзор сервиса: от описания идеи до PDF с scoring и рекомендациями.",
  backgroundImageUrl: "",
  layout: "split" as const,
  primaryLabel: "Создать отчёт",
  primaryHref: "/reports/create",
  embedUrl: "",
};

export const DEFAULT_FAQ = {
  sectionId: "faq",
  title: "Частые вопросы",
  backgroundImageUrl: "",
  items: LANDING_FAQ_ITEMS.map((item) => ({ question: item.q, answer: item.a })),
};

export const DEFAULT_NEWS = {
  sectionId: "news",
  title: "Важные новости Идеалитики",
  subtitle: "Здесь все важные новости по Идеалитике",
  backgroundImageUrl: "",
  layout: "grid" as const,
  showFilters: true,
  filterAllLabel: "Все",
  items: [
    {
      title: "Запуск сервиса Идеалитика",
      description:
        "Desk research, Wordstat и скоринг go/no-go для рынка РФ — теперь за минуты, а не недели.",
      date: "12 августа 2026",
      href: "/news/launch",
      coverUrl: "/brand/hero-bg-dark.png",
      category: "Продукт",
    },
    {
      title: "Как читать scoring в отчёте",
      description:
        "Краткий гид по шести критериям и уровню уверенности источников.",
      date: "10 августа 2026",
      href: "/news/scoring-guide",
      coverUrl: "/brand/hero-bg-light.png",
      category: "Методология",
    },
    {
      title: "Обновление брендбука",
      description: "Новый фирменный стиль Idealitika на сайте и в письмах.",
      date: "8 августа 2026",
      href: "/news/brandbook",
      coverUrl: "/brand/logo-lab/logo-concept-prism-data.png",
      category: "Компания",
    },
  ],
};

export const DEFAULT_NEWS_HERO = {
  sectionId: "article-hero",
  eyebrow: "Новости",
  title: "Design is a solution to a problem. Art is a question to a problem",
  lead: "",
  author: "Команда Идеалитики",
  date: "12 августа 2026",
  coverUrl: "/brand/hero-bg-dark.png",
  showBackLink: true,
  listHref: "/news",
  listLabel: "Все новости",
  markImageUrl: "",
  bylineColor: "#FFFFFF",
};

export const DEFAULT_NEWS_H2 = {
  sectionId: "article-h2",
  heading: "Раздел H2 первый",
  body: "Мы работаем каждый день, чтобы проверка бизнес-идеи занимала минуты, а не недели — со scoring, рынком РФ и ссылками на источники.",
};

export const DEFAULT_NEWS_IMAGE = {
  sectionId: "article-image",
  imageUrl: "/brand/logo-lab/logo-concept-prism-data.png",
  alt: "",
};

export const DEFAULT_BREADCRUMBS = {
  sectionId: "breadcrumbs",
  items: [
    { label: "Главная", href: "/" },
    { label: "Новости", href: "/news" },
    { label: "Статья", href: "" },
  ],
  linkColor: "#E11D48",
  currentColor: "#1D1D1F",
  separatorColor: "#7C7C80",
  separator: "/",
  align: "start" as const,
};

export const DEFAULT_PROMO_BANNER = {
  sectionId: "promo-banner",
  href: "/#pricing",
  imageUrl: "",
  imageUrlDark: "",
  widthMode: "container" as const,
  heightMode: "auto" as const,
  heightPx: 120,
};

export const DEFAULT_FOOTER = {
  tagline:
    "Аналитика бизнес-идей: desk research, Wordstat и скоринг go/no-go для рынка РФ",
  columns: createDefaultFooterColumns([
    { href: "/#pricing", label: "Тарифы", group: "product" },
    { href: "/legal/offer", label: "Договор-оферта", group: "legal" },
    {
      href: "/legal/privacy",
      label: "Согласие на обработку персональных данных",
      group: "legal",
    },
    { href: "/legal/cookies", label: "Политика Cookies", group: "legal" },
    {
      href: "/legal/newsletter",
      label: "Согласие на рассылку",
      group: "legal",
    },
  ]),
};

export type LandingCardItem = {
  icon: ResearchIconName;
  title: LandingTextValue;
  text: LandingTextValue;
};

/** Дефолты props по типу блока — для миграции старых страниц с пустыми `{}`. */
export const LANDING_BLOCK_DEFAULTS: Record<string, Record<string, unknown>> = {
  Hero: DEFAULT_HERO,
  HeroSplit: DEFAULT_HERO_SPLIT,
  Pas: DEFAULT_PAS,
  Pains: DEFAULT_PAINS,
  Audience: DEFAULT_AUDIENCE,
  Features: {
    sectionId: "method",
    title: "Что внутри отчёта",
    subtitle:
      "Не обёртка, а содержание: scoring, рынок, конкуренты, спрос и план следующих шагов.",
    backgroundImageUrl: "",
    layout: "grid",
    items: DEFAULT_FEATURES_ITEMS,
  },
  RowGrid: DEFAULT_ROW_GRID,
  Video: DEFAULT_VIDEO,
  Cases: DEFAULT_CASES,
  Faq: DEFAULT_FAQ,
  Partners: DEFAULT_PARTNERS,
  News: DEFAULT_NEWS,
  NewsHero: DEFAULT_NEWS_HERO,
  NewsH2: DEFAULT_NEWS_H2,
  NewsImage: DEFAULT_NEWS_IMAGE,
  ContactsHero: DEFAULT_CONTACTS_HERO,
  YandexMap: DEFAULT_YANDEX_MAP,
  LinkList: DEFAULT_LINK_LIST,
  Breadcrumbs: DEFAULT_BREADCRUMBS,
  PromoBanner: DEFAULT_PROMO_BANNER,
  Footer: DEFAULT_FOOTER,
};
